<template>
  <div class="debug-container">
    <label for="all-session-messages-container">Mensagens da sessão</label>

    <div id="all-session-messages-container" class="messages-container">
      <Icon
        v-if="!allSessionMessages.length"
        icon="ph:empty"
        class="mx-auto"
        width="40"
        height="40"
      />

      <div
        v-for="(message, index) in allSessionMessages"
        :class="message.type"
        :key="index"
        class="message"
      >
        <div class="message-header">
          <span class="message-time">{{ formatTime(message.timestamp) }}</span>

          <span class="message-type">{{ message.type }}</span>
        </div>

        <div class="message-content">
          <pre>{{ formatMessage(message.data) }}</pre>
        </div>
      </div>
    </div>

    <label for="debugging-messages-container">Terminal de Debugging</label>

    <div id="debugging-messages-container" class="terminal-container">
      <div class="terminal-header">
        <div class="terminal-controls">
          <span class="control-dot red" />

          <span class="control-dot yellow" />

          <span class="control-dot green" />
        </div>
        <div class="terminal-title">debug@crm-websocket:</div>
      </div>

      <div class="terminal-body">
        <div v-if="!debuggingMessages.length" class="terminal-empty">
          <div class="terminal-prompt">
            <span class="prompt-user">debug@crm-websocket</span>
          </div>

          <div class="terminal-text">
            Aguardando mensagens de debugging<Icon
              icon="svg-spinners:3-dots-fade"
              width="15"
              height="15"
            />
          </div>
        </div>

        <div
          v-for="(message, index) in debuggingMessages"
          :class="['terminal-message']"
          :key="index"
        >
          <div class="terminal-prompt">
            <Icon :icon="getCommandIcon(message.type)" width="25" height="25" />

            <span>{{ message.type }}</span>
          </div>

          <div class="terminal-output">
            <div class="output-timestamp">[{{ formatTime(message.timestamp) }}]</div>

            <pre class="output-content">{{ formatMessage(message.data) }}</pre>
          </div>
        </div>
      </div>
    </div>

    <div class="controls">
      <button @click="$emit('send-ping')" :disabled="!isConnected" class="btn btn-primary">
        📤 Enviar Ping
      </button>

      <button @click="$emit('clear-messages')" class="btn btn-danger">
        🧹 Limpar todas as mensagens
      </button>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue2";

export default {
  name: "WebsocketDebug",

  components: {
    Icon,
  },

  props: {
    allSessionMessages: {
      type: Array,
      required: true,
    },

    debuggingMessages: {
      type: Array,
      required: true,
    },

    isConnected: {
      type: Boolean,
      required: true,
    },
  },

  methods: {
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleTimeString("pt-BR");
    },

    formatMessage(data) {
      if (typeof data === "string") return data;

      return JSON.stringify(data, null, 2);
    },

    getCommandIcon(type) {
      const prefixes = {
        "http-request-debug": "ic:baseline-http",
        "websocket-connection-error-debug": "clarity:disconnected-solid",
        "websocket-disconnected-debug": "clarity:disconnected-solid",
        "http-request-error-debug": "ic:baseline-error",
        "websocket-message-sent-error-debug": "ic:baseline-error",
        "websocket-error-debug": "ic:baseline-error",
        "websocket-message-sent-debug": "icon-park-outline:email-push",
        "websocket-message-received-debug": "icon-park-outline:email-down",
        "websocket-connected-debug": "wpf:connected",
      };

      return prefixes[type] || prefixes.default;
    },
  },
};
</script>

<style lang="scss" scoped>
.debug-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 15px;

  .controls {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    width: 100%;
    gap: 10px;

    .btn {
      transition: all 0.2s ease;
      font-weight: 500;
      font-size: 14px;
      padding: 10px 20px;
      border-radius: 6px;
      border: none;
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      &:not(:disabled):hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      &.btn-primary {
        background: #007bff;
        color: #ffffff;

        &:hover:not(:disabled) {
          background: #0056b3;
        }
      }

      &.btn-danger {
        background: #dc3545;
        color: white;

        &:hover:not(:disabled) {
          background: #c82333;
        }
      }
    }
  }

  .messages-container {
    display: flex;
    flex-direction: column;
    max-height: 400px;
    gap: 10px;
    padding: 10px;
    margin-bottom: 10px;
    border-radius: 6px;
    border: 1px solid #d9d9d9;
    background: white;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      border-radius: 4px;
      background: #f1f1f1;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background: #888;

      &:hover {
        background: #555;
      }
    }

    .message {
      padding: 12px;
      border-radius: 6px;
      border: 1px solid #d9d9d9;

      &.sent {
        max-width: 80%;
        margin-left: auto;
        border-color: #b3d9ff;
        background: #e7f3ff;
      }

      &.received {
        max-width: 80%;
        margin-right: auto;
        border-color: #c8e6c9;
        background: #e8f5e9;
      }

      &.error {
        border-color: #ffcdd2;
        background: #ffebee;
      }

      &.system {
        font-style: italic;
        border-color: #e0e0e0;
        background: #f5f5f5;
      }

      &.http-response {
        border-color: #ffe0b2;
        background: #fff3e0;
      }

      .message-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;
        font-size: 11px;
        color: #6c757d;

        .message-type {
          text-transform: uppercase;
          font-weight: 600;
        }
      }

      .message-content {
        pre {
          font-family: "Courier New", monospace;
          font-size: 12px;
          white-space: pre-wrap;
          word-break: break-word;
          margin: 0;
        }
      }
    }
  }
}

.terminal-container {
  display: flex;
  flex-direction: column;
  font-family: "Fira Code", "Consolas", "Monaco", "Courier New", monospace;
  max-height: 500px;
  border-radius: 8px;
  border: 1px solid #30363d;
  background: #0d1117;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;

  .terminal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    border-bottom: 1px solid #30363d;
    background: linear-gradient(135deg, #161b22 0%, #0d1117 100%);

    .terminal-controls {
      display: flex;
      gap: 8px;

      .control-dot {
        width: 12px;
        height: 12px;
        border-radius: 50%;

        &.red {
          background: #ff5f56;
          box-shadow: 0 0 8px rgba(255, 95, 86, 0.3);
        }

        &.yellow {
          background: #ffbd2e;
          box-shadow: 0 0 8px rgba(255, 189, 46, 0.3);
        }

        &.green {
          background: #27ca3f;
          box-shadow: 0 0 8px rgba(39, 202, 63, 0.3);
        }
      }
    }

    .terminal-title {
      font-size: 14px;
      font-weight: 600;
      text-shadow: 0 0 10px rgba(88, 166, 255, 0.3);
      color: #f535aa;
    }
  }

  .terminal-body {
    flex: 1;
    max-height: 400px;
    padding: 16px;
    background: #0d1117;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      border-radius: 4px;
      background: #161b22;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background: #58a6ff;
      box-shadow: 0 0 8px rgba(88, 166, 255, 0.3);

      &:hover {
        background: #79c0ff;
      }
    }

    .terminal-empty {
      display: flex;
      flex-direction: column;
      gap: 8px;
      opacity: 0.8;

      .terminal-prompt {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 14px;

        .prompt-user {
          color: #f535aa;
          font-weight: 600;
        }
      }

      .terminal-text {
        color: #8b949e;
        font-style: italic;
      }
    }

    .terminal-message {
      transition: all 0.3s ease;
      padding: 10px;
      margin-bottom: 16px;
      border-left: 3px solid transparent;

      &:hover {
        border-left-color: #f535aa;
        background: rgba(88, 166, 255, 0.05);
      }

      .terminal-prompt {
        display: flex;
        align-items: center;
        font-weight: 600;
        font-size: 14px;
        gap: 4px;
        margin-bottom: 10px;
        color: #f535aa;
      }

      .terminal-output {
        padding: 12px;
        margin-left: 20px;
        border-radius: 4px;
        border: 1px solid #30363d;
        background: rgba(13, 17, 23, 0.8);

        .output-timestamp {
          font-family: "Fira Code", monospace;
          font-size: 11px;
          margin-bottom: 8px;
          color: #d6e4ff;
        }

        .output-content {
          font-family: "Fira Code", "Consolas", "Monaco", "Courier New", monospace;
          font-size: 12px;
          line-height: 1.5;
          white-space: pre-wrap;
          word-break: break-word;
          padding: 10px;
          margin: 0;
          color: #000000;
        }
      }
    }
  }
}

label {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: -5px;
  color: #31333f;
}
</style>
